import {
  AVAILABLE_DEFECT_LAYERS_PATH,
  DASHBOARD_STATS_PATH,
} from '../contracts/httpPaths';
import {
  AvailableDefectLayersQuery,
  AvailableDefectLayersResponseBody,
} from '../contracts/availableLayers';
import {
  SurveyDataManagerDashboardStatsQuery,
  SurveyDataManagerDashboardStatsResponseBody,
} from '../contracts/dashboardStats';
import { getRefreshDataHeader } from 'utils/getRefreshDataHeader';

import { mergeDefectOverlayCatalogDefects } from '../defectOverlays/mergeDefectOverlayCatalogs';
import type { DefectOverlayTenantOptions } from '../defectOverlays/tenantDefectOverlayAdapter';
import { surveyDataManagerClient, toSurveyDataManagerApiError } from './httpClient';
import { MOCK_SCANNER_DEFECTS } from './scannerCatalog.mock';
import { isScannerMockCatalogueEnabled } from './scannerMockResponses';

export { isScannerMockCatalogueEnabled } from './scannerMockResponses';

function withScannerMockCatalogue(
  body: AvailableDefectLayersResponseBody,
  query: AvailableDefectLayersQuery = {},
): AvailableDefectLayersResponseBody {
  if (!isScannerMockCatalogueEnabled()) {
    return body;
  }

  const surveyTypeFilters = (query.surveyTypes ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  const mockDefects =
    surveyTypeFilters.length === 0
      ? MOCK_SCANNER_DEFECTS
      : MOCK_SCANNER_DEFECTS.filter((defect) =>
          surveyTypeFilters.includes(defect.surveyType.trim().toLowerCase()),
        );

  const mockSlugs = new Set(mockDefects.map((defect) => String(defect.slug)));
  return {
    defects: [
      ...mockDefects,
      ...body.defects.filter((defect) => !mockSlugs.has(String(defect.slug))),
    ],
  };
}

function appendDashboardStatsQueryParams(
  params: URLSearchParams,
  query: SurveyDataManagerDashboardStatsQuery,
): void {
  if (query.surveyType) {
    params.append('surveyTypes', query.surveyType);
  }
  if (query.surveyYear) {
    params.append('surveyYears', query.surveyYear);
  }
  if (query.lane) {
    params.append('lane', query.lane);
  }
}

export async function getDefects(
  query: AvailableDefectLayersQuery = {},
  refreshData: boolean = false,
  signal?: AbortSignal,
  options?: DefectOverlayTenantOptions,
): Promise<AvailableDefectLayersResponseBody> {
  const params = new URLSearchParams();

  query.surveyTypes?.forEach((value) => {
    if (value) params.append('surveyTypes', value);
  });
  query.surveyYears?.forEach((value) => {
    if (value) params.append('surveyYears', value);
  });

  try {
    const response = await surveyDataManagerClient.get<AvailableDefectLayersResponseBody>(
      AVAILABLE_DEFECT_LAYERS_PATH,
      {
        ...getRefreshDataHeader(refreshData),
        params,
        signal,
      },
    );
    const withScanner = withScannerMockCatalogue(response.data, query);
    return {
      defects: mergeDefectOverlayCatalogDefects(
        withScanner.defects,
        query.surveyTypes,
        options,
      ),
    };
  } catch (error) {
    if (isScannerMockCatalogueEnabled()) {
      return {
        defects: mergeDefectOverlayCatalogDefects(
          withScannerMockCatalogue({ defects: [] }, query).defects,
          query.surveyTypes,
          options,
        ),
      };
    }
    const overlayOnly = mergeDefectOverlayCatalogDefects(
      [],
      query.surveyTypes,
      options,
    );
    if (overlayOnly.length > 0) {
      return { defects: overlayOnly };
    }
    throw toSurveyDataManagerApiError(error);
  }
}

export async function getDefectsStatistics(
  query: SurveyDataManagerDashboardStatsQuery = {},
  refreshData: boolean = false,
  signal?: AbortSignal,
): Promise<SurveyDataManagerDashboardStatsResponseBody> {
  const params = new URLSearchParams();
  appendDashboardStatsQueryParams(params, query);

  try {
    const response = await surveyDataManagerClient.get<SurveyDataManagerDashboardStatsResponseBody>(
      DASHBOARD_STATS_PATH,
      {
        ...getRefreshDataHeader(refreshData),
        params,
        signal,
      },
    );
    return response.data;
  } catch (error) {
    throw toSurveyDataManagerApiError(error);
  }
}
